// frontend/src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // La sesión debe usar el mismo backend público cuando el CRM se abre mediante
  // un túnel, y el backend local/LAN en los accesos internos.
  const API_URL = window.__CRM_API__ || 'http://localhost:8000/api';

  // Configurar axios para incluir el token automáticamente
  const setupAxios = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Verificar si hay sesión al cargar
  // La restauración de sesión debe ejecutarse una sola vez al iniciar la aplicación.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const usuarioGuardado = localStorage.getItem('usuario') || sessionStorage.getItem('usuario');

    if (token && usuarioGuardado) {
      try {
        const usuarioData = JSON.parse(usuarioGuardado);
        setUsuario(usuarioData);
        setupAxios(token);
        axios.get(`${API_URL}/auth/me`).then((response) => {
          const refreshed = response.data;
          const store = localStorage.getItem('token') ? localStorage : sessionStorage;
          store.setItem('usuario', JSON.stringify(refreshed));
          setUsuario(refreshed);
        }).catch((requestError) => {
          const status = requestError?.response?.status;
          // A temporary Render restart, timeout or 5xx must not erase a valid
          // local session. Only an explicit authentication rejection proves
          // that the stored token can no longer be used.
          if (status === 401 || status === 403) {
            localStorage.removeItem('token'); localStorage.removeItem('usuario');
            sessionStorage.removeItem('token'); sessionStorage.removeItem('usuario');
            setupAxios(null);
            setUsuario(null);
          } else {
            setError('El servidor se está recuperando. Conservamos tu sesión y volveremos a intentarlo automáticamente.');
          }
        });
      } catch (e) {
        localStorage.removeItem('token'); localStorage.removeItem('usuario');
        sessionStorage.removeItem('token'); sessionStorage.removeItem('usuario');
      }
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantiene la jornada diaria basada en actividad real del navegador. El
  // backend limita el tiempo contado a la ventana laboral de Los Ángeles.
  useEffect(() => {
    if (!usuario) return undefined;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
    let lastPulse = 0;
    const heartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastPulse < 60000) return;
      lastPulse = now;
      axios.post(`${API_URL}/auth/workday/heartbeat`, { browser_timezone: timezone }).catch(() => {});
    };
    const onActivity = () => heartbeat();
    const onVisibility = () => { if (document.visibilityState === 'visible') heartbeat(); };
    ['pointerdown', 'scroll', 'keydown', 'touchstart'].forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    heartbeat();
    return () => {
      ['pointerdown', 'scroll', 'keydown', 'touchstart'].forEach((eventName) => window.removeEventListener(eventName, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [usuario, API_URL]);

  // Login
  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      formData.append('remember_me', String(rememberMe));

      // No fijar Content-Type manualmente: el navegador incluye el boundary
      // correcto de FormData, necesario también cuando la app se usa por HTTPS.
      const response = await axios.post(`${API_URL}/auth/login`, formData);

      const { access_token, usuario } = response.data;

      // Guardar en localStorage
      const store = rememberMe ? localStorage : sessionStorage;
      localStorage.removeItem('token'); localStorage.removeItem('usuario');
      sessionStorage.removeItem('token'); sessionStorage.removeItem('usuario');
      store.setItem('token', access_token);
      store.setItem('usuario', JSON.stringify(usuario));

      // Configurar axios
      setupAxios(access_token);

      setUsuario(usuario);
      return { success: true, usuario };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.userMessage || 'El servidor no respondió. Espera unos segundos e intenta nuevamente.';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Registro
  const register = async (nombre, apellido, email, password, rol = 'Paralegal') => {
    try {
      setError(null);
      
      const response = await axios.post(`${API_URL}/auth/register`, {
        nombre,
        apellido,
        email,
        password,
        rol,
      });

      return { success: true, usuario: response.data };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al registrar usuario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('usuario');
    sessionStorage.removeItem('token'); sessionStorage.removeItem('usuario');
    setupAxios(null);
    setUsuario(null);
    setError(null);
  };

  const actualizarUsuario = (cambios) => {
    setUsuario(actual => {
      const next = { ...actual, ...cambios };
      const store = localStorage.getItem('token') ? localStorage : sessionStorage;
      store.setItem('usuario', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    usuario,
    loading,
    error,
    login,
    register,
    logout,
    actualizarUsuario,
    isAuthenticated: !!usuario,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
